import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MessageCircle, ArrowRight, CheckCircle, Shield,
  Building2, Percent, Clock, Heart, Ban, Scale, CreditCard, Globe,
  ChevronRight, Play, Star, Zap, Send, MessageSquare
} from 'lucide-react';
import { Logo } from '../common';
import Footer from '../common/Footer';
import FlowbotDemoEmulator from '../common/FlowbotDemoEmulator';
import FlowbotLaunchOptIn from '../common/FlowbotLaunchOptIn';
import LegacyToolBanner from '../common/LegacyToolBanner';
import { FF_WHOLESALE_ENABLED } from '../../config/featureFlags';

// Two-tone text formatting components
const ForwardsflowText = ({ variant = 'default', bold = true }) => (
  <span className={`font-sans ${bold ? 'font-extrabold' : 'font-normal'} inline-block`}>
    <span className={variant === 'white' ? 'text-white' : 'text-black'}>Forwards</span>
    <span className={variant === 'white' ? 'text-zinc-300' : 'text-zinc-500'}>flow</span>
  </span>
);

const FlowBotText = ({ variant = 'default', bold = true }) => (
  <span className={`font-sans ${bold ? 'font-extrabold' : 'font-normal'} inline-block`}>
    <span className={variant === 'white' ? 'text-white' : 'text-black'}>Flow</span>
    <span className={variant === 'white' ? 'text-zinc-300' : 'text-zinc-500'}>Bot</span>
  </span>
);

const formatBrandText = (text, variant = 'default', bold = true) => {
  if (typeof text !== 'string') return text;
  const tokens = [];
  let currentIdx = 0;
  const regex = /(Forwardsflow|FlowBot)/gi;
  let match;
  
  while ((match = regex.exec(text)) !== null) {
    if (match.index > currentIdx) {
      tokens.push(text.substring(currentIdx, match.index));
    }
    const matchedText = match[1];
    if (matchedText.toLowerCase() === 'forwardsflow') {
      tokens.push(<ForwardsflowText key={match.index} variant={variant} bold={bold} />);
    } else {
      tokens.push(<FlowBotText key={match.index} variant={variant} bold={bold} />);
    }
    currentIdx = regex.lastIndex;
  }
  
  if (currentIdx < text.length) {
    tokens.push(text.substring(currentIdx));
  }
  return tokens.length > 0 ? tokens : text;
};

const FlowbotPage = () => {
  const [activeTab, setActiveTab] = useState('invest'); // 'invest' or 'save'
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  const [whatsappTriggerCount, setWhatsappTriggerCount] = useState(0);

  const investFeatures = [
    'No multiple application downloads',
    'Single KYC upload',
    'Multiple regulated reputable counterparties',
    'Realtime T-bill investment & maturity terms negotiation',
    'Competitive yields (vs. 84–400% APR predatory market)',
    'No exorbitant non-interest rate fees',
    'Transparent fee structure',
    'Available on WhatsApp, Telegram, and WebChat'
  ];

  const saveFeatures = [
    'Access savings pools of 5% to 7.5% per month, compounding monthly',
    'Pool with other savers to hit bank minimums from $1 equivalent',
    'Sustainable treasury-backed saving ecosystem with transparent returns',
    'Choose your term: call, 30-day, 90-day, 180-day',
    'Automated savings discipline tools',
    'Instant visibility into pool progress and rate lock',
    'Withdrawal flexibility with laddered liquidity'
  ];

  const flowbotSteps = [
    { step: 1, title: 'Open FlowBot™', description: 'Start a conversation on WhatsApp or Telegram', icon: MessageCircle },
    { step: 2, title: 'Tell us what you need', description: 'Choose to invest or join a high-yield wholesale saving pool', icon: CreditCard },
    { step: 3, title: 'Banks compete or aggregate', description: 'Repay terms are bid in real-time or deposits at scale aggregated', icon: Building2 },
    { step: 4, title: 'Compare & choose', description: 'Review competing offers or choose deposit maturity options', icon: Scale },
    { step: 5, title: 'Instant settlement', description: 'Funds disbursed via M-PESA or locked in wholesale savings pool', icon: Zap },
  ];

  return (
    <div className="min-h-screen bg-white">
      <LegacyToolBanner />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-20">
            <Logo />
            <div className="hidden lg:flex items-center gap-6">
              <Link to="/" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Home</Link>
              <Link to="/flowbot" className="font-mono text-xs uppercase tracking-[0.15em] text-black font-bold border-b-2 border-black pb-1">{formatBrandText("FlowBot™")}</Link>
              <Link to="/apply" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Apply for Access</Link>
              <Link to="/demo" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-700 hover:text-black flex items-center gap-1.5 px-3 py-1.5 border border-zinc-200 hover:border-[#4D4D4D] transition-all">
                <Play className="w-3 h-3 fill-black text-black" /> Book a Demo
              </Link>
              <Link to="/pricing" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Pricing</Link>
              <Link to="/contact" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Contact</Link>

              {/* Conditional Nav Links */}
              {FF_WHOLESALE_ENABLED && (
                <>
                  <Link to="/marketplace" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Marketplace</Link>
                  <Link to="/investor/login" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 hover:text-black transition-colors">Investor Login</Link>
                </>
              )}

              <Link to="/login" className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-800 hover:text-black font-medium">Platform Login</Link>
              <Link to="/csr" className="font-mono text-xs uppercase tracking-[0.15em] text-green-600 hover:text-green-700 transition-colors">CSR</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section id="flowbot-hero-demo" className="pt-36 pb-20 px-4 sm:px-6 lg:px-8 text-black bg-zinc-50 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto">
          {/* Header row - Badge is kept above the grid */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 border border-zinc-300 rounded-full font-mono text-xs uppercase tracking-[0.15em] text-zinc-600 mb-8">
            <MessageCircle className="w-4 h-4" />
            {formatBrandText("FlowBot™ | Invest Smart. Save Smarter.")}
          </div>

          {/* 2-Column Grid */}
          <div className="grid lg:grid-cols-2 gap-12 items-stretch">
            {/* Column 1: Left */}
            <div className="lg:flex lg:flex-col lg:justify-between lg:h-full">
              <div className="mb-6 lg:mb-8">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6 font-sans">
                  {formatBrandText("FlowBot™")}
                </h1>
                <p className="text-sm font-sans text-gray-600 leading-relaxed">
                  {formatBrandText("Fair mobile T-bill investments where banks and financial institutions compete for your business. And now, wholesale-rate savings pools where you team up with other savers to access rates only big depositors get. Because T-bill investments deliver strong short-term treasury yields, they are able to finance monthly compounding savings rates as well, delivering more of the benefits of that economy to savers. One app. One KYC. Zero predatory fees.™")}
                </p>
              </div>

              {/* Tabs Box (strictly at the bottom of Left Column, aligned with right-side emulator bottom) */}
              <div className="border border-gray-900 rounded-none mb-6 lg:mb-0 overflow-hidden bg-white">
                <div className="flex border-b border-gray-900 bg-zinc-50">
                  <button
                    onClick={() => setActiveTab('invest')}
                    className={`flex-1 py-3 text-center text-xs uppercase tracking-wider font-bold font-mono transition-all ${
                      activeTab === 'invest' ? 'bg-[#4D4D4D] text-white font-bold' : 'text-gray-500 hover:text-black'
                    }`}
                  >
                    Invest (T-Bill Yields)
                  </button>
                  <button
                    onClick={() => setActiveTab('save')}
                    className={`flex-1 py-3 text-center text-xs uppercase tracking-wider font-bold font-mono transition-all ${
                      activeTab === 'save' ? 'bg-[#4D4D4D] text-white font-bold' : 'text-gray-500 hover:text-black'
                    }`}
                  >
                    Save (Deposit Pooling)
                  </button>
                </div>
                <div className="p-6">
                  {activeTab === 'invest' ? (
                    <ul className="space-y-3">
                      {investFeatures.map((feat, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-xs text-gray-700 font-mono">
                          <CheckCircle className="w-4 h-4 text-black flex-shrink-0 mt-0.5" />
                          <span>{formatBrandText(feat)}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <ul className="space-y-3">
                      {saveFeatures.map((feat, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-xs text-gray-700 font-mono">
                          <CheckCircle className="w-4 h-4 text-black flex-shrink-0 mt-0.5" />
                          <span>{formatBrandText(feat)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
            
            {/* Column 2: Right (Only containing FlowBot interactive demo) */}
            <div className="flex flex-col items-center w-full">
              <FlowbotDemoEmulator showHeading={false} />
            </div>
          </div>

          {/* Under grid section: pricing statement & action button aligned at bottom */}
          <div className="grid lg:grid-cols-2 gap-12 mt-8 lg:items-end">
            <div className="flex items-end">
              <p className="text-xs text-zinc-500 font-mono leading-relaxed mb-0">
                {formatBrandText("FlowBot™ is free for all consumers and always will be.")}
              </p>
            </div>
            <div className="flex justify-center w-full mt-4 lg:mt-0">
              <a href="#flowbot-hero-demo" className="w-full max-w-[340px] px-8 py-4 bg-[#4D4D4D] text-white font-mono text-xs uppercase tracking-[0.15em] font-medium border border-[#4D4D4D] hover:bg-black transition-all flex items-center justify-center gap-2">
                <Play className="w-4 h-4 fill-white text-white" /> SEE {formatBrandText("FlowBot™", "white")} IN ACTION
              </a>
            </div>
          </div>

          {/* Stretched Out Launch Signup Panel */}
          <div className="mt-16 pt-12 border-t border-zinc-200">
            <FlowbotLaunchOptIn variant="stretched" />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-20 bg-white border-b border-zinc-200 text-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 font-sans">{formatBrandText("How FlowBot™ works")}</h2>
            <p className="text-sm text-gray-600 max-w-2xl mx-auto">{formatBrandText("Get competitive T-bill yields or join a pooled wholesale savings group in 5 simple steps.")}</p>
          </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
              {flowbotSteps.map((step, idx) => (
                <div key={idx} className="border border-zinc-300 p-6 bg-white transition-all hover:border-[#4D4D4D] hover:-translate-y-0.5 relative text-left">
                  <span className="absolute top-6 right-6 font-mono text-zinc-300 text-[10px] font-bold">0{step.step}</span>
                  <div className="w-10 h-10 border border-zinc-300 flex items-center justify-center mb-6 bg-zinc-50">
                    <step.icon className="w-5 h-5 text-black" />
                  </div>
                  <h3 className="text-xs font-bold text-gray-900 mb-2 font-sans">{formatBrandText(step.title)}</h3>
                  <p className="text-[11px] text-zinc-500 leading-relaxed font-sans">{formatBrandText(step.description)}</p>
                </div>
              ))}
            </div>
        </div>
      </section>


      {/* Access Flowbot Anywhere */}
      <section className="py-20 bg-zinc-50 text-black border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 font-sans">{formatBrandText("Access FlowBot™ anywhere")}</h2>
            <p className="text-sm text-gray-600 max-w-2xl mx-auto">{formatBrandText("Use FlowBot™ through your preferred channel. No app downloads required.")}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { name: 'Web chat', description: 'Access directly from any browser on desktop or mobile', icon: Globe, status: 'Available Now', color: 'bg-[#4D4D4D]', link: '#flowbot-hero-demo' },
              { name: 'Telegram', description: 'Full FlowBot™ experience on Telegram with inline keyboards', icon: Send, status: 'Available Now', color: 'bg-[#0088cc]', link: 'telegram' },
              { name: 'WhatsApp', description: 'Interact with FlowBot™ seamlessly via WhatsApp Business API.', icon: MessageSquare, status: 'Coming Soon', color: 'bg-[#25D366]', link: 'whatsapp-notify' },
            ].map((channel, idx) => (
              <div
                key={idx}
                onClick={() => {
                  if (channel.link === "telegram") {
                    setShowTelegramModal(true);
                  } else if (channel.link === "whatsapp-notify") {
                    setWhatsappTriggerCount(prev => prev + 1);
                    document.getElementById("flowbot-launch-signup-stretched")?.scrollIntoView({ behavior: "smooth" });
                  } else if (channel.link) {
                    document.querySelector(channel.link)?.scrollIntoView({ behavior: "smooth" });
                  }
                }}
                className={`bg-white rounded-none p-8 border border-zinc-300 text-center hover:shadow-none transition-all cursor-pointer hover:border-[#4D4D4D] hover:-translate-y-0.5`}
              >
                <div className={`w-16 h-16 ${channel.color} rounded-none flex items-center justify-center mx-auto mb-4`}><channel.icon className="w-8 h-8 text-white" /></div>
                <span className={`inline-block px-3 py-1 ${channel.status === "Available Now" ? "bg-zinc-100 text-zinc-800" : "bg-gray-100 text-gray-600"} text-[10px] font-mono font-semibold rounded-none mb-3`}>{channel.status}</span>
                <h3 className="text-lg font-bold text-gray-900 mb-2 font-sans">{formatBrandText(channel.name)}</h3>
                <p className="text-xs text-gray-600 mb-4 font-sans leading-relaxed">{formatBrandText(channel.description)}</p>
                {channel.link && (
                  <span className="inline-flex items-center gap-2 text-zinc-800 font-bold font-mono text-xs uppercase hover:underline">
                    {channel.link === "telegram" 
                      ? "Open in Telegram" 
                      : channel.link === "whatsapp-notify" 
                        ? "Notify me when WhatsApp version launches." 
                        : "Try Now"}{" "}
                    <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* Stretched Out Launch Signup Panel */}
          <div id="flowbot-launch-signup-stretched" className="scroll-mt-24">
            <FlowbotLaunchOptIn variant="stretched" whatsappTriggerCount={whatsappTriggerCount} />
          </div>
        </div>
      </section>

      <Footer />

      {/* Telegram Modal */}
      {showTelegramModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm" onClick={() => setShowTelegramModal(false)}>
          <div className="bg-white rounded-none border border-black max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-none" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <FlowbotDemoEmulator showHeading={false} />
              <div className="mt-6 space-y-3">
                <p className="text-sm text-gray-500 text-center font-medium font-mono">{formatBrandText("Access FlowBot™ on Telegram")}</p>
                <a
                  href="https://t.me/Forwardsflowbot"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full px-4 py-3 text-white font-semibold rounded-none hover:opacity-90 transition-opacity text-center font-mono text-xs uppercase"
                  style={{ backgroundColor: '#0088cc' }}
                >
                  Open in Telegram App
                </a>
              </div>
              <button
                onClick={() => setShowTelegramModal(false)}
                className="mt-4 w-full text-gray-500 hover:text-gray-700 text-sm text-center font-mono"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlowbotPage;
